import { useContext, FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import styles from './SbSharedRelatedItems.scss';

import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';

import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import {
    SbSharedThinBrowseItemTrackingProvider,
    SbSharedThinBrowseItemTrackingContext,
} from '../SbSharedThinBrowseItemTrackingProvider/SbSharedThinBrowseItemTrackingProvider';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

import {
    SbSharedRelatedItems_itemSearch$key,
    SbSharedRelatedItems_itemSearch$data,
} from './__generated__/SbSharedRelatedItems_itemSearch.graphql';
import {
    SbSharedRelatedItems_viewer$key,
    SbSharedRelatedItems_viewer$data,
} from './__generated__/SbSharedRelatedItems_viewer.graphql';

type SbSharedRelatedItemsProps = {
    itemSearch: SbSharedRelatedItems_itemSearch$data;
    isClient: boolean;
    viewer: SbSharedRelatedItems_viewer$data;
};

const SbSharedRelatedItemsComponent: FC<SbSharedRelatedItemsProps> = ({
    itemSearch,
    isClient,
    viewer,
}) => {
    const relatedItems = itemSearch?.relatedItems?.edges || [];
    const { fireThinBrowseItemsImpressionTracking, fireItemClickTracking } = useContext(
        SbSharedThinBrowseItemTrackingContext
    );

    const title = (
        <FormattedMessage id="sb.SbSharedRelatedItems.title" defaultMessage="Related Items" />
    );

    if (relatedItems.length > 0) {
        return (
            <ServerVarsContextConsumer>
                {({ isMobile }) => (
                    <>
                        <BarHeader title={title} />
                        <QuickViewProvider>
                            {({ quickView }) => {
                                return (
                                    <div className={styles.container} data-tn="related-items">
                                        {relatedItems.map((relatedItem, index) => {
                                            const { item } = relatedItem?.node || {};
                                            if (item) {
                                                return (
                                                    <SharedItemTile
                                                        isClient={isClient}
                                                        key={index}
                                                        index={index}
                                                        item={item}
                                                        showSellerName={false}
                                                        viewer={viewer}
                                                        quickView={quickView}
                                                        itemSearch={itemSearch}
                                                        isMobile={isMobile}
                                                        onContentVisible={() =>
                                                            fireThinBrowseItemsImpressionTracking(
                                                                index
                                                            )
                                                        }
                                                        onClick={() => fireItemClickTracking(index)}
                                                    />
                                                );
                                            } else {
                                                return null;
                                            }
                                        })}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    </>
                )}
            </ServerVarsContextConsumer>
        );
    }
    return null;
};

type Props = {
    itemSearch: SbSharedRelatedItems_itemSearch$key;
    isClient: boolean;
    viewer: SbSharedRelatedItems_viewer$key;
};

export const SbSharedRelatedItems: FC<Props> = ({
    itemSearch: itemSearchRef,
    isClient,
    viewer: viewerRef,
}) => {
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRelatedItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...SbSharedThinBrowseItemTrackingProvider_itemSearch
                relatedItems: youMayAlsoLike(first: 12) {
                    edges {
                        node {
                            item {
                                ...SharedItemTile_item
                                    @arguments(
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                    )
                                ...SbSharedThinBrowseItemTrackingProvider_items
                                ...shippingCostHelpers_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );
    const viewer = useFragment(
        graphql`
            fragment SbSharedRelatedItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...SbSharedThinBrowseItemTrackingProvider_viewer
            }
        `,
        viewerRef
    );

    const relatedItems = itemSearch?.relatedItems?.edges || [];
    if (relatedItems.length === 0) {
        return null;
    }

    const items = relatedItems?.map(edge => edge?.node?.item).filter(filterFalsy);

    return (
        <SbSharedThinBrowseItemTrackingProvider
            itemSearch={itemSearch}
            viewer={viewer}
            items={items}
            thinBrowseListingType="related"
        >
            <SbSharedRelatedItemsComponent
                itemSearch={itemSearch}
                isClient={isClient}
                viewer={viewer}
            />
        </SbSharedThinBrowseItemTrackingProvider>
    );
};
